const FULL_PROFILE = Object.freeze({
  name: 'full',
  lowPower: false,
  antialias: true,
  shadows: true,
  pixelRatioCap: 1.6,
  pixelRatioFloor: 1.1,
  radarSize: 360,
  radarHz: 30,
  simulationHz: 60,
  maxSimulationSteps: 6
});

const MOBILE_PREMIUM_PROFILE = Object.freeze({
  name: 'mobile-premium',
  lowPower: false,
  antialias: true,
  shadows: true,
  pixelRatioCap: 1.5,
  pixelRatioFloor: 1,
  radarSize: 300,
  radarHz: 24,
  simulationHz: 60,
  maxSimulationSteps: 4
});

const CONSTRAINED_PROFILE = Object.freeze({
  name: 'constrained',
  lowPower: true,
  antialias: false,
  shadows: false,
  pixelRatioCap: 1.15,
  pixelRatioFloor: 0.9,
  radarSize: 240,
  radarHz: 18,
  simulationHz: 50,
  maxSimulationSteps: 4
});

export function choosePerformanceProfile({
  width = 1280,
  coarsePointer = false,
  hardwareConcurrency = 8,
  deviceMemory = 8
} = {}) {
  const genuinelyConstrained = (
    hardwareConcurrency <= 2 ||
    deviceMemory <= 2 ||
    (hardwareConcurrency <= 4 && deviceMemory <= 4)
  );
  if (genuinelyConstrained) return CONSTRAINED_PROFILE;
  if (coarsePointer || width < 900) return MOBILE_PREMIUM_PROFILE;
  return FULL_PROFILE;
}

export function getBrowserPerformanceProfile() {
  return choosePerformanceProfile({
    width: window.innerWidth,
    coarsePointer: window.matchMedia('(pointer: coarse)').matches,
    hardwareConcurrency: navigator.hardwareConcurrency ?? 8,
    deviceMemory: navigator.deviceMemory ?? 8
  });
}

export class AdaptiveQualityController {
  constructor(profile, devicePixelRatio = 1) {
    this.profile = profile;
    this.devicePixelRatio = devicePixelRatio;
    this.currentRatio = Math.min(devicePixelRatio, profile.pixelRatioCap);
    this.windowSeconds = 0;
    this.windowFrames = 0;
    this.slowWindows = 0;
    this.fastWindows = 0;
    this.lastFps = 60;
    this.adjustments = 0;
  }

  setDevicePixelRatio(devicePixelRatio) {
    this.devicePixelRatio = devicePixelRatio;
    const cap = Math.min(devicePixelRatio, this.profile.pixelRatioCap);
    this.currentRatio = Math.min(this.currentRatio, cap);
    return this.currentRatio;
  }

  observeFrame(frameSeconds) {
    if (!Number.isFinite(frameSeconds) || frameSeconds <= 0 || frameSeconds > 0.2) return null;
    this.windowSeconds += frameSeconds;
    this.windowFrames += 1;
    if (this.windowSeconds < 2) return null;
    const fps = this.windowFrames / this.windowSeconds;
    this.windowSeconds = 0;
    this.windowFrames = 0;
    return this.observeWindow(fps);
  }

  observeWindow(fps) {
    this.lastFps = fps;
    if (fps < 43) {
      this.slowWindows += 1;
      this.fastWindows = 0;
    } else if (fps > 56) {
      this.fastWindows += 1;
      this.slowWindows = 0;
    } else {
      this.slowWindows = 0;
      this.fastWindows = 0;
    }

    if (this.slowWindows >= 2) {
      this.slowWindows = 0;
      return this.adjust(-0.15);
    }
    if (this.fastWindows >= 4) {
      this.fastWindows = 0;
      return this.adjust(0.1);
    }
    return null;
  }

  adjust(delta) {
    const cap = Math.min(this.devicePixelRatio, this.profile.pixelRatioCap);
    const floor = Math.min(cap, this.profile.pixelRatioFloor);
    const next = Math.max(floor, Math.min(cap, this.currentRatio + delta));
    if (Math.abs(next - this.currentRatio) < 0.001) return null;
    this.currentRatio = Number(next.toFixed(2));
    this.adjustments += 1;
    return this.currentRatio;
  }
}
